/**
 * The optional purchasing features a facility has switched on in Purchasing Setup (Jeff,
 * 2026-10-20: "Purchasing still seems way too complicated"). The core pages (Order Guides,
 * Purchase Orders, Suppliers, Price Updates, Spend, Setup) are always there; each feature adds a
 * group of pages to the menu, the top-bar search and their routes. Mirrors the C#
 * PurchasingFacilityFeatures (tblPur_FacilityFeatures).
 *
 * Fails closed: until the answer arrives, and if the request fails (an API without the endpoint
 * answers 404), every optional feature counts as off.
 */
import { useContext } from 'react';
import { QueryClient, QueryClientContext, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from '../client';
import { useGlobalStore } from '../../stores/globalStore';
import { useNotificationStore } from '../../stores/notificationStore';
import { PURCHASING, apiErrorMessage } from './common';

export type PurchasingFeature = 'receiving' | 'inventory' | 'planning' | 'contractsEdi' | 'analytics';

export interface PurchasingFacilityFeatures {
  receivingEnabled: boolean;
  inventoryEnabled: boolean;
  planningEnabled: boolean;
  contractsEdiEnabled: boolean;
  analyticsEnabled: boolean;
}

/** Each feature: its switch, its name, and the pages it adds (Purchasing Setup lists these). */
export const PURCHASING_FEATURES: { key: PurchasingFeature; field: keyof PurchasingFacilityFeatures; label: string; pages: string }[] = [
  { key: 'receiving', field: 'receivingEnabled', label: 'Receiving and invoices', pages: 'Adds Receiving, Vendor Invoices and Credit Memos.' },
  { key: 'inventory', field: 'inventoryEnabled', label: 'Inventory and waste', pages: 'Adds Inventory (items, transactions, counts, floor stock), Inventory Planning and Waste.' },
  { key: 'planning', field: 'planningEnabled', label: 'Planning', pages: 'Adds Reorder, Demand Planning, Requisitions and Item Links.' },
  { key: 'contractsEdi', field: 'contractsEdiEnabled', label: 'Contracts and EDI', pages: 'Adds Contracts and EDI.' },
  { key: 'analytics', field: 'analyticsEnabled', label: 'Analytics', pages: 'Adds Costing, Prep Schedule, Allergens, Supplier Scorecard, Corporate and Item Links.' },
];

export const NO_PURCHASING_FEATURES: PurchasingFacilityFeatures = {
  receivingEnabled: false,
  inventoryEnabled: false,
  planningEnabled: false,
  contractsEdiEnabled: false,
  analyticsEnabled: false,
};

export const purchasingFeaturesUrl = (facilityId: number) => `/api/purchasing/features/${facilityId}`;

/** The Purchasing Setup tab where the features are switched on. */
export const PURCHASING_FEATURES_SETUP = '/ordering/setup?tab=features';

export const featuresKey = (facilityId: number) => [PURCHASING, 'features', facilityId] as const;

/** Whether a feature (or any of several) is on. */
export function featureOn(
  features: PurchasingFacilityFeatures,
  feature: PurchasingFeature | readonly PurchasingFeature[],
): boolean {
  const keys = Array.isArray(feature) ? feature : [feature as PurchasingFeature];
  return keys.some((k) => !!features[PURCHASING_FEATURES.find((f) => f.key === k)!.field]);
}

/** The feature's name, for the "turned off" notice. */
export function featureLabel(feature: PurchasingFeature | readonly PurchasingFeature[]): string {
  const keys = Array.isArray(feature) ? feature : [feature as PurchasingFeature];
  return keys.map((k) => PURCHASING_FEATURES.find((f) => f.key === k)!.label).join(' and ');
}

// The menu reads this in places that may render without a QueryClientProvider (tests).
const detachedClient = new QueryClient();

/**
 * The facility's switches. `loaded` is false until the answer (or a failure) is in; `features`
 * is all off until then. Not requested unless `enabled` (the facility is licensed for purchasing).
 */
export function usePurchasingFeatures(facilityId: number, enabled = true): { features: PurchasingFacilityFeatures; loaded: boolean } {
  const contextClient = useContext(QueryClientContext);
  const on = enabled && !!contextClient && facilityId > 0;
  const { data, isError, isFetched } = useQuery(
    {
      queryKey: featuresKey(facilityId),
      queryFn: async () => ({ ...NO_PURCHASING_FEATURES, ...(await apiClient.get<PurchasingFacilityFeatures>(purchasingFeaturesUrl(facilityId))).data }),
      enabled: on,
      staleTime: 5 * 60 * 1000,
      retry: false,
    },
    contextClient ?? detachedClient,
  );
  return { features: data ?? NO_PURCHASING_FEATURES, loaded: !on || isFetched || isError };
}

/**
 * Whether a feature (or any of several) is on for the signed-in facility. False while loading,
 * so an in-page link into a switched-off page never shows and then works.
 */
export function usePurchasingFeatureOn(feature: PurchasingFeature | readonly PurchasingFeature[]): boolean {
  const facilityId = useGlobalStore((s) => s.facilityId);
  const { features } = usePurchasingFeatures(facilityId);
  return featureOn(features, feature);
}

export function useSavePurchasingFeatures(facilityId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (features: PurchasingFacilityFeatures) => {
      await apiClient.put(purchasingFeaturesUrl(facilityId), features);
      return features;
    },
    onSuccess: (features) => {
      qc.setQueryData(featuresKey(facilityId), features);
      useNotificationStore.getState().addNotification('Purchasing features saved', 'success');
    },
    onError: (err) => useNotificationStore.getState().addNotification(apiErrorMessage(err), 'error'),
  });
}
